import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import CustomButton from "../../components/UI/Buttons/CustomButton";
import Location from "../../components/UI/Icons/Location";
import RightArrow from "../../components/UI/Icons/RightArrow";
import { TCheckTokenToValidateEmail } from "../../models/form";
import { useAppDispatch as useDispatch } from "../../store";
import { hideTransparentPreloaderAction, showTransparentPreloaderAction } from "../../store/application/slice";
import {
  checkEmailVerifyThunk,
  getJobDetailsBasedOnAddressThunk,
} from "../../store/auth/thunks";
import {
  TCheckEmailVerifyResponse,
  TGetJobDetailsBasedOnAddressResponse,
} from "../../store/auth/types";
import { NON_AUTH_ROUTE } from "../App/routes";

const EmailVerification: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [jobDetailsBasedOnResponse, setJobDetailsBasedOnResponse] = useState<
    TGetJobDetailsBasedOnAddressResponse | undefined
  >(undefined);
  const [linkNotValid, setLinkNotValid] = useState(false);

  useEffect(() => {
    const urlSearchString = window.location.search;
    const params: URLSearchParams = new URLSearchParams(urlSearchString);

    dispatch(showTransparentPreloaderAction())
    const checkToken = async () => {
      const checkValidTokenData: TCheckTokenToValidateEmail = {
        // TODO simplify
        token: params.get("token") as string,
        email: params.get("email") as string,
      };
      const responseData = await dispatch(
        checkEmailVerifyThunk(checkValidTokenData),
      );

      let selectedAddress = "";
      if(isFulfilled(responseData)) {
        const payload = responseData?.payload as TCheckEmailVerifyResponse;
        selectedAddress = payload?.data?.address?.address ?? "";
        const linkNotValid = payload?.message === "Invalid token.";
        if(linkNotValid) {
          setLinkNotValid(true);
        }

        const response = await dispatch(
          getJobDetailsBasedOnAddressThunk(selectedAddress),
        );
        if (isFulfilled(response)) {
          setJobDetailsBasedOnResponse(
            response.payload as TGetJobDetailsBasedOnAddressResponse,
          ); // ✅ Ensure correct type
        }
      } else {
        // API returned 4xx/5xx — invalid or expired token
        setLinkNotValid(true);
      }
      dispatch(hideTransparentPreloaderAction())

      

      localStorage.setItem("verified_email", checkValidTokenData.email);
    };

    checkToken();
  }, [dispatch]);

  const handleContinue = () => {
    navigate(NON_AUTH_ROUTE.EMAIL_VERIFIED);
  };



  return (
    <>
      {jobDetailsBasedOnResponse && !linkNotValid &&
      jobDetailsBasedOnResponse.total_centres > 0 ? (
        <>
          <div className="d-flex flex-column gap-3 align-items-center">
            <div className="position-relative">
              <div className="w-100px h-100px bg-teal-muted rounded-circle">
                <div className="position-absolute top-50 start-50 translate-middle">
                  <Location size={30} color="#E52514" />
                </div>
                <div className="bg-alert-light py-8px px-12px radius-8px position-absolute top-100 start-50 translate-middle">
                  <div className="fs-16 fw-400 text-black">
                    {jobDetailsBasedOnResponse.state_name}
                  </div>
                </div>
              </div>
            </div>
            <div className="h-30px"></div>
            <div className="fs-22 fw-400 text-black">
              There are{" "}
              <span className="fw-700">
                {jobDetailsBasedOnResponse.total_centres} centres
              </span>
            </div>
            {jobDetailsBasedOnResponse.total_active_vacancies > 0 ? (
              <div className="fs-22 fw-700 text-black">
                and {jobDetailsBasedOnResponse.total_active_vacancies} jobs
                available
              </div>
            ) : (
              ""
            )}
            <div className="fs-22 fw-400 text-black">around your area!</div>
          </div>
          <div className="h-1px bg-grey-light"></div>
        </>
      ) : (
        ""
      )}


      {!linkNotValid ? (      
      <div className="d-flex flex-column gap-30">
        <div className="fs-16 fw-400 text-black">
          Welcome to Connected Crew! We're excited to have you here.
        </div>
        <div className="fs-16 fw-400 text-black">
          Our screening process is designed to ensure only suitable, qualified
          and compliant casual staff join our casual workforce.
        </div>
        <div className="fs-16 fw-400 text-black">
          It consists of three formal stages, with each step reviewed by our
          team. At the end of each stage, your documentation will be verified,
          and your progress will be reflected on your profile, which will also
          be visible to centres considering you for shifts.
        </div>
        <div className="fs-16 fw-400 text-black">
          Each stage has its own set of requirements, and you'll need to
          complete one before moving to the next.
        </div>
        <div className="fs-16 fw-400 text-black">
          Take your time, follow the prompts carefully, and ensure all
          information is accurate and up to date.
        </div>
        <div className="fs-16 fw-400 text-black">
          Good luck! We look forward to seeing you on the Crew!
        </div>
        <div className="d-flex flex-column gap-25">
          <div className="d-flex flex-column gap-8">
            <div className="d-flex">
              <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                Stage 1
              </div>
            </div>
            <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
              <div className="fs-16 fw-700 text-black lh-1">
                Personal Details &amp; Interview
              </div>
              <div className="fs-13 fw-700 text-grey-dark lh-1">30min</div>
            </div>
            <ul className="m-0">
              <li>ID Verification</li>
              <li>Personal Details</li>
              <li>Qualifications</li>
              <li>Job Preferences</li>
              <li>Online Interview</li>
            </ul>
          </div>
          <div className="d-flex flex-column gap-8">
            <div className="d-flex">
              <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                Stage 2
              </div>
            </div>
            <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
              <div className="fs-16 fw-700 text-black lh-1">
                Compliance Checks
              </div>
              <div className="fs-13 fw-700 text-grey-dark lh-1">40min</div>
            </div>
            <ul className="m-0">
              <li>Police Check</li>
              <li>Working With Children</li>
              <li>Right to Work in Australia</li>
              <li>Reference Check</li>
            </ul>
          </div>
          <div className="d-flex flex-column gap-8">
            <div className="d-flex">
              <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                Stage 3
              </div>
            </div>
            <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
              <div className="fs-16 fw-700 text-black lh-1">
                Documents &amp; Contract
              </div>
              <div className="fs-13 fw-700 text-grey-dark lh-1">30min</div>
            </div>
            <ul className="m-0">
              <li>TFN Declaration</li>
              <li>Superannuation</li>
              <li>Contract</li>
            </ul>
          </div>
        </div>
        <div className="fs-16 fw-400 text-black">
          Between each stage, it will take <strong>up to 1 business day</strong>{" "}
          for a manual review to be completed by our consultants.
        </div>
        <div className="d-flex justify-content-center">
          <CustomButton
            contentClass={"d-flex"}
            type="button"
            variant="primary"
            className="btn-theme bg-teal text-black w-250px"
            onClick={handleContinue}
          >
            Continue <RightArrow color={"black"} />
          </CustomButton>
        </div>
      </div>
      ) : (
        <div className="d-flex flex-column gap-30 gap-mobile-20 align-items-center">
        <div className="d-flex flex-column gap-15 align-items-center">

          <svg width="50" height="51" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M2.92892 17.0711C-0.976328 13.1658 -0.976328 6.83418 2.92892 2.92893C6.83416 -0.976312 13.1658 -0.976312 17.0711 2.92893C20.9763 6.83418 20.9763 13.1658 17.0711 17.0711C13.1658 20.9763 6.83416 20.9763 2.92892 17.0711ZM4.21457 4.21458C1.01937 7.40978 1.01937 12.5902 4.21457 15.7854C7.40977 18.9806 12.5902 18.9806 15.7854 15.7854C18.9806 12.5902 18.9806 7.40978 15.7854 4.21458C12.5902 1.01938 7.40977 1.01938 4.21457 4.21458ZM6.78586 6.78588C7.14088 6.43086 7.71649 6.43086 8.07151 6.78588L9.99998 8.71435L11.9285 6.78588C12.2835 6.43086 12.8591 6.43086 13.2141 6.78588C13.5691 7.1409 13.5691 7.7165 13.2141 8.07153L11.2856 10L13.2141 11.9285C13.5691 12.2835 13.5691 12.8591 13.2141 13.2141C12.8591 13.5691 12.2835 13.5691 11.9285 13.2141L9.99998 11.2856L8.07151 13.2141C7.71649 13.5691 7.14088 13.5691 6.78586 13.2141C6.43084 12.8591 6.43084 12.2835 6.78586 11.9285L8.71434 10L6.78586 8.07153C6.43084 7.71651 6.43084 7.1409 6.78586 6.78588Z" fill="black"/>
          </svg>

          <div className="fs-16 fw-400 text-black text-center">
            Invalid or expired link <br />
             Please contact support for assistance.
             <br />
           
          </div>
        </div>
        
      </div>
      )}
    </>
  );
};

export default EmailVerification;
